import { Link, useParams } from 'react-router-dom';

import { useOutputs, useStream } from 'src/api/queries';
import { ReaderShell } from 'src/components/editorial/ReaderShell';
import { Skeleton } from 'src/components/ui/Skeleton';
import { OUTPUT_STATUS_LABEL, outputMetrics, readerConfig } from 'src/lib/reader';
import type { EditorialOutput, EditorialOutputContent } from 'src/types/editorial';
import { LoadFailure } from 'src/components/editorial/LoadFailure';

/**
 * The same story in every profile, side by side. Everything here comes from payloads the reader
 * already loaded — no extra API call.
 *
 * Columns are ordered by model then profile id, never by any metric: ranking the versions is
 * exactly the nudge Art Direction §14 forbids. The metrics are shown so an editor can rank them.
 */

const MODEL_COLORS = ['#8c1a3d', '#1b4b8f', '#4a7c3f', '#8a5a10', '#5a6472'];

const STATE_TONE: Record<string, string> = {
  pending_human_review: 'ok',
  validation_failed: 'bad',
  blocked_eligibility: 'warn',
  generation_failed: 'bad',
};

export function ComparisonWorkspace() {
  const { streamId = '', transformationId = '' } = useParams<{ streamId: string; transformationId: string }>();
  const outputs = useOutputs(streamId);
  const stream = useStream(streamId);

  if (outputs.isPending) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={null} comparing>
        <Skeleton variant="rows" count={4} />
      </ReaderShell>
    );
  }

  if (outputs.isError) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={null} comparing>
        <LoadFailure title="No se pudieron cargar las versiones a comparar" error={outputs.error} onRetry={outputs.refetch} />
      </ReaderShell>
    );
  }

  const all = outputs.data?.outputs ?? [];
  const current = all.find(item => item.transformationId === transformationId);

  if (!current) {
    return (
      <ReaderShell streamId={streamId} output={null} stream={stream.data?.stream ?? null} comparing>
        <div className="r-empty">
          <strong>No encontramos esta nota</strong>
          La versión ya no existe en esta transmisión.
        </div>
      </ReaderShell>
    );
  }

  const versions = [...all.filter(item => item.storyId === current.storyId)].sort((a, b) => {
    const modelA = outputMetrics(a).model;
    const modelB = outputMetrics(b).model;
    return modelA === modelB ? a.profileId.localeCompare(b.profileId) : modelA.localeCompare(modelB);
  });

  const models = [...new Set(versions.map(version => outputMetrics(version).model))];
  const colorFor = (model: string) => MODEL_COLORS[models.indexOf(model) % MODEL_COLORS.length]!;
  const withText = versions.filter(version => version.payload.content).length;
  const headline =
    current.payload.content?.headline ??
    versions.find(version => version.payload.content)?.payload.content?.headline ??
    'Esta historia';

  return (
    <ReaderShell streamId={streamId} output={current} stream={stream.data?.stream ?? null} comparing>
      <header className="cmp-head">
        <h1>{headline}</h1>
        {models.length > 1 ? (
          <p>
            Estas versiones se redactaron con <strong>{models.length} modelos distintos</strong>. Las diferencias de
            longitud, cobertura de citas y fidelidad son comparables: los hechos, la verificación y el enriquecimiento
            son los mismos.
          </p>
        ) : (
          <p>
            Las {versions.length} versiones salieron del mismo modelo (<strong>{models[0] ?? 'desconocido'}</strong>)
            con perfiles editoriales distintos. Para comparar modelos, vuelve a correr la transformación con otro{' '}
            <code>JUDGE_LLM_MODEL</code>: el modelo queda guardado en cada salida.
          </p>
        )}
      </header>

      <dl className="cmp-brief">
        <div>
          <dt>Versiones</dt>
          <dd>{versions.length}</dd>
        </div>
        <div>
          <dt>Con texto</dt>
          <dd>{withText}</dd>
        </div>
        <div>
          <dt>Modelos</dt>
          <dd>{models.length}</dd>
        </div>
        <div>
          <dt>Historia</dt>
          <dd>{current.storyId.slice(-8)}</dd>
        </div>
      </dl>

      <div className="cmp-scroll">
        <div className={`cmp-grid${versions.length <= 3 ? ' few' : ''}`}>
          {versions.map(version => (
            <ComparisonColumn
              key={version.transformationId}
              output={version}
              current={current}
              streamId={streamId}
              modelColor={colorFor(outputMetrics(version).model)}
            />
          ))}
        </div>
      </div>

      <p className="cmp-note">
        «Por claim» divide las palabras entre los claims que la nota realmente citó: sobre la misma evidencia, más
        palabras es relleno, no cobertura. «Fidelidad» es la proporción de bloques que el validador marcó{' '}
        <em>supported</em> — y ese validador corre en un modelo distinto al que redactó, porque un modelo que califica
        su propia prosa no es un control.
      </p>
    </ReaderShell>
  );
}

function ComparisonColumn({
  output,
  current,
  streamId,
  modelColor,
}: {
  output: EditorialOutput;
  current: EditorialOutput;
  streamId: string;
  modelColor: string;
}) {
  const payload = output.payload;
  const config = readerConfig(output);
  const metrics = outputMetrics(output);
  const isCurrent = output.transformationId === current.transformationId;

  return (
    <article className={`cmp-col${isCurrent ? ' current' : ''}`}>
      <header className="cmp-col-head">
        <p className="cmp-model">
          <span className="cmp-dot" style={{ background: modelColor }} />
          {metrics.model}
        </p>
        <p className="cmp-profile">
          {config.label}
          {config.treatment && ` · ${config.treatment}`}
        </p>
        <div className="cmp-flags">
          <span className="cmp-flag" data-tone={STATE_TONE[payload.status] ?? 'warn'}>
            {OUTPUT_STATUS_LABEL[payload.status] ?? payload.status}
          </span>
          {isCurrent && <span className="cmp-flag here">Estás aquí</span>}
        </div>
      </header>

      <dl className="cmp-stats">
        <div>
          <dt>Palabras</dt>
          <dd>{metrics.words}</dd>
        </div>
        <div>
          <dt>Por claim</dt>
          <dd>{metrics.perClaim ?? '—'}</dd>
        </div>
        <div>
          <dt>Citados</dt>
          <dd>
            {metrics.cited}
            <span className="cmp-of">/{metrics.prose}</span>
          </dd>
        </div>
        <div>
          <dt>Fidelidad</dt>
          <dd>
            {metrics.fidelity === null ? (
              '—'
            ) : (
              <>
                {metrics.fidelity}
                <span className="cmp-of">%</span>
              </>
            )}
          </dd>
        </div>
        <div>
          <dt>Bloqueos</dt>
          <dd>{metrics.blocking}</dd>
        </div>
        <div>
          <dt>Segundos</dt>
          <dd>{metrics.seconds ?? '—'}</dd>
        </div>
      </dl>

      {payload.content ? (
        <div className="cmp-nota">
          <h3 className="cmp-h">{payload.content.headline}</h3>
          {payload.content.deck && <p className="cmp-deck">{payload.content.deck}</p>}
          <ComparisonBlocks content={payload.content} />
        </div>
      ) : (
        <div className="cmp-empty">
          Esta versión no generó texto. {OUTPUT_STATUS_LABEL[payload.status] ?? payload.status}.
        </div>
      )}

      <footer className="cmp-foot">
        <span>{metrics.claims} claims usados</span>
        <span>
          {metrics.revisions} {metrics.revisions === 1 ? 'revisión' : 'revisiones'}
        </span>
      </footer>

      {!isCurrent && (
        <Link
          className="cmp-open"
          to={`/nota/${encodeURIComponent(streamId)}/${encodeURIComponent(output.transformationId)}`}
        >
          Abrir esta versión →
        </Link>
      )}
    </article>
  );
}

function ComparisonBlocks({ content }: { content: EditorialOutputContent }) {
  return (
    <>
      {(content.body_blocks ?? []).map((block, index) => {
        const cited = (block.claim_ids ?? []).length > 0 || (block.fact_ids ?? []).length > 0 ? '1' : undefined;
        if (block.type === 'heading') {
          return (
            <h4 key={index} className="cmp-sub">
              {block.text}
            </h4>
          );
        }
        if (block.type === 'quote') {
          return (
            <blockquote key={index} className="cmp-q" data-cited={cited}>
              {block.text}
            </blockquote>
          );
        }
        return (
          <p key={index} className="cmp-p" data-cited={cited}>
            {block.text}
          </p>
        );
      })}
    </>
  );
}
